import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdminStore } from '../store/adminStore';
import { authFetch } from '../services/auth';
import { NavIcon } from '../components/admin/NavIcons';
import '../styles/admin.css';

interface LeadLite {
  orderReference: string;
  name: string;
  packageName: string;
  status: string;
  createdAt: string;
}

type Segment = { label: string; value: number; color: string };

// A compact donut with a centred total. Segments with value 0 are skipped; an
// empty donut shows a neutral ring.
const Donut: React.FC<{ segments: Segment[]; centerLabel: string }> = ({ segments, centerLabel }) => {
  const total = segments.reduce((s, x) => s + x.value, 0);
  const r = 52;
  const stroke = 18;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg viewBox="0 0 140 140" className="dash-donut" role="img">
      <g transform="rotate(-90 70 70)">
        <circle cx={70} cy={70} r={r} fill="none" stroke="#eef0f3" strokeWidth={stroke} />
        {total > 0 &&
          segments
            .filter((s) => s.value > 0)
            .map((s, i) => {
              const len = (s.value / total) * c;
              const el = (
                <circle
                  key={i}
                  cx={70}
                  cy={70}
                  r={r}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={stroke}
                  strokeDasharray={`${len} ${c - len}`}
                  strokeDashoffset={-offset}
                  strokeLinecap="butt"
                />
              );
              offset += len;
              return el;
            })}
      </g>
      <text x={70} y={66} textAnchor="middle" fontSize={26} fontWeight={700} fill="#111827">
        {total}
      </text>
      <text x={70} y={86} textAnchor="middle" fontSize={11} fill="#6b7280">
        {centerLabel}
      </text>
    </svg>
  );
};

const DonutCard: React.FC<{ title: string; segments: Segment[]; centerLabel: string; footer?: React.ReactNode }> = ({
  title,
  segments,
  centerLabel,
  footer,
}) => {
  const total = segments.reduce((s, x) => s + x.value, 0);
  return (
    <div className="dash-chart-card">
      <div className="dash-chart-head">
        <h2 className="dash-chart-title">{title}</h2>
      </div>
      <div className="dash-donut-row">
        <Donut segments={segments} centerLabel={centerLabel} />
        <ul className="dash-donut-legend">
          {segments.map((s) => (
            <li key={s.label} className="dash-legend-row">
              <span className="dash-legend-dot" style={{ background: s.color }} />
              <span className="dash-legend-label">{s.label}</span>
              <span className="dash-legend-count">{s.value}</span>
              <span className="dash-legend-pct">{total > 0 ? Math.round((s.value / total) * 100) : 0}%</span>
            </li>
          ))}
        </ul>
      </div>
      {footer && <div className="dash-chart-footer">{footer}</div>}
    </div>
  );
};

export const AdminDashboardPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { orders, ordersResponse, loading, error, fetchOrders, clearError } = useAdminStore();

  const [leads, setLeads] = useState<LeadLite[]>([]);

  useEffect(() => {
    fetchOrders(1, 500);
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeads = async () => {
    try {
      const res = await authFetch('/api/admin/landing/leads');
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads || []);
      }
    } catch {
      /* dashboard tolerates a missing leads feed */
    }
  };

  const has = (s: string, ...v: string[]) => v.some((x) => s.toLowerCase() === x.toLowerCase());
  const ordersTotal = ordersResponse?.totalCount ?? orders.length;
  const ordersPending = orders.filter((o) => has(o.status, 'pending')).length;
  const ordersInProgress = orders.filter((o) => has(o.status, 'inprogress')).length;
  const ordersCompleted = orders.filter((o) => has(o.status, 'completed')).length;
  const ordersCancelled = orders.filter((o) => has(o.status, 'cancelled')).length;

  const leadsTotal = leads.length;
  const leadsPaid = leads.filter((l) => l.status === 'PAID').length;
  const leadsPending = leadsTotal - leadsPaid;
  const conversion = leadsTotal > 0 ? Math.round((leadsPaid / leadsTotal) * 100) : 0;

  const orderSegments: Segment[] = [
    { label: t('admin:statusPending'), value: ordersPending, color: '#f59e0b' },
    { label: t('admin:statusInProgress'), value: ordersInProgress, color: '#3b82f6' },
    { label: t('admin:statusCompleted'), value: ordersCompleted, color: '#16a34a' },
    { label: t('admin:statusCancelled'), value: ordersCancelled, color: '#ef4444' },
  ];
  const leadSegments: Segment[] = [
    { label: t('admin:leadStatusPaid'), value: leadsPaid, color: '#16a34a' },
    { label: t('admin:leadStatusPending'), value: leadsPending, color: '#f59e0b' },
  ];

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>{t('admin:adminDashboard')}</h1>
        <p className="admin-subtitle">{t('admin:dashboardSubtitle')}</p>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={clearError}>×</button>
        </div>
      )}

      {loading && orders.length === 0 ? (
        <div className="loading">{t('admin:loading')}</div>
      ) : (
        <>
          {/* ===================== Operations ===================== */}
          <section className="dashboard-group">
            <h2 className="dashboard-group-title">{t('admin:groupOperations')}</h2>

            <div className="stats-grid">
              <Link to="/admin/orders" className="stat-card stat-card-link">
                <div className="stat-value">{ordersTotal}</div>
                <div className="stat-label">{t('admin:totalOrders')}</div>
                <div className="stat-breakdown">
                  <span className="stat-active">{ordersPending} {t('admin:statusPending')}</span>
                  {ordersInProgress > 0 && (
                    <span className="stat-neutral">{ordersInProgress} {t('admin:statusInProgress')}</span>
                  )}
                </div>
              </Link>

              <Link to="/admin/leads" className="stat-card stat-card-link">
                <div className="stat-value">{leadsTotal}</div>
                <div className="stat-label">{t('admin:totalLeads')}</div>
                <div className="stat-breakdown">
                  <span className="stat-active">{leadsPaid} {t('admin:leadStatusPaid')}</span>
                  {leadsPending > 0 && (
                    <span className="stat-inactive">{leadsPending} {t('admin:leadStatusPending')}</span>
                  )}
                </div>
              </Link>
            </div>

            <div className="action-grid">
              <Link to="/admin/orders" className="action-card">
                <div className="action-icon"><NavIcon name="orders" size={26} /></div>
                <h3>{t('admin:manageOrders')}</h3>
                <p>{t('admin:viewManageAllCustomerOrders')}</p>
                <div className="action-stats">{ordersTotal} {t('admin:orders')}</div>
              </Link>

              <Link to="/admin/leads" className="action-card">
                <div className="action-icon"><NavIcon name="leads" size={26} /></div>
                <h3>{t('admin:leadsTitle')}</h3>
                <p>{t('admin:leadsSubtitle')}</p>
                <div className="action-stats">{leadsTotal} {t('admin:leadsStatLabel')}</div>
              </Link>
            </div>
          </section>

          {/* ===================== Analytics ===================== */}
          <section className="dashboard-group">
            <h2 className="dashboard-group-title">{t('admin:groupAnalytics')}</h2>
            <div className="dash-charts-grid">
              <DonutCard
                title={t('admin:chartOrdersTitle')}
                segments={orderSegments}
                centerLabel={t('admin:orders')}
              />
              <DonutCard
                title={t('admin:chartLeadsPaymentTitle')}
                segments={leadSegments}
                centerLabel={t('admin:leadsStatLabel')}
                footer={
                  <span>
                    {t('admin:chartConversion')}: <strong>{conversion}%</strong>
                  </span>
                }
              />
            </div>
          </section>
        </>
      )}
    </div>
  );
};
